import { CreativeInput, Creative } from 'API';
import { API } from 'aws-amplify';
import { storeQueriesV2 } from 'graphql/mutations';
import { getStoredQuery } from 'graphql/queries';
import { useCustomReportContext } from 'context/CustomReportContext';
import { useCallback } from 'react';

export type CreativeData = {
  dataSourceName: string | null;
  creative: Creative | null;
  data?: any[] | null;
  outputLocation?: string | null;
  title?: string | null;
};

/**
 * Hook to save creative data using storeQueriesV2 mutation
 * Uses CustomReportID as campaign_id and SectionItemID as position
 */
export const useSaveCustomReportCreative = (): readonly [
  (position: string, dataSourceName: string, creatives: CreativeInput[], title?: string) => Promise<string>,
] => {
  const { activeCustomReport } = useCustomReportContext();

  const saveCreative = useCallback(
    async (position: string, dataSourceName: string, creatives: CreativeInput[], title?: string): Promise<string> => {
      try {
        const response = (await API.graphql({
          query: storeQueriesV2,
          variables: {
            campaign_id: activeCustomReport?.CustomReportID?.toString(),
            position: position,
            query_id: dataSourceName,
            creatives: creatives,
            title: title,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as any;

        // Check if response exists - that means success
        if (response?.data?.storeQueriesV2) {
          return 'success';
        }
        return 'error';
      } catch (error) {
        console.error('useSaveCustomReportCreative - Error:', error);
        return 'error';
      }
    },
    [activeCustomReport?.CustomReportID],
  );

  return [saveCreative] as const;
};

/**
 * Hook to save creative data for duplication (with explicit reportId)
 * Used when duplicating custom reports to specify target report ID
 */
export const useSaveCustomReportCreativeForDuplication = (): readonly [
  (
    position: string,
    dataSourceName: string,
    creatives: CreativeInput[],
    reportId: number,
    title?: string,
  ) => Promise<string>,
] => {
  const saveCreative = useCallback(
    async (
      position: string,
      dataSourceName: string,
      creatives: CreativeInput[],
      reportId: number,
      title?: string,
    ): Promise<string> => {
      try {
        const response = (await API.graphql({
          query: storeQueriesV2,
          variables: {
            campaign_id: reportId.toString(),
            position: position,
            query_id: dataSourceName,
            creatives: creatives,
            title: title,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as any;

        if (response?.data?.storeQueriesV2) {
          return 'success';
        }
        return 'error';
      } catch (error) {
        console.error('useSaveCustomReportCreativeForDuplication - Error:', error);
        return 'error';
      }
    },
    [],
  );

  return [saveCreative] as const;
};

/**
 * Hook to fetch creative data using getStoredQuery
 */
export const useGetStoredCreative = (): readonly [(position: string) => Promise<CreativeData | null>] => {
  const { activeCustomReport } = useCustomReportContext();

  const fetchCreative = useCallback(
    async (position: string): Promise<CreativeData | null> => {
      try {
        const response = (await API.graphql({
          query: getStoredQuery,
          variables: {
            campaign_id: activeCustomReport?.CustomReportID?.toString(),
            position: position,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as any;

        const result = response?.data?.getStoredQuery;

        if (result && result.creatives && result.creatives.length > 0) {
          return {
            dataSourceName: result.query_id || null,
            creative: result.creatives[0] as Creative,
            outputLocation: result.query_id || null,
            title: result.title || null,
          };
        }

        return null;
      } catch (error) {
        console.error('useGetStoredCreative - Error:', error);
        return null;
      }
    },
    [activeCustomReport?.CustomReportID],
  );

  return [fetchCreative] as const;
};

/**
 * Hook to fetch creative data with explicit reportId (for duplication)
 */
export const useGetStoredCreativeByReportId = (): readonly [
  (position: string, reportId: number) => Promise<CreativeData | null>,
] => {
  const fetchCreative = useCallback(async (position: string, reportId: number): Promise<CreativeData | null> => {
    try {
      const response = (await API.graphql({
        query: getStoredQuery,
        variables: {
          campaign_id: reportId.toString(),
          position: position,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as any;

      const result = response?.data?.getStoredQuery;

      if (result && result.creatives && result.creatives.length > 0) {
        return {
          dataSourceName: result.query_id || null,
          creative: result.creatives[0] as Creative,
          outputLocation: result.query_id || null,
          title: result.title || null,
        };
      }

      return null;
    } catch (error) {
      console.error('useGetStoredCreativeByReportId - Error:', error);
      return null;
    }
  }, []);

  return [fetchCreative] as const;
};
